// SWR hooks. Unit 8 landed `useProjects`; Unit 17 adds `usePaper`,
// `useAnnotations`, and `usePaperStatus` (the §8 polling hook).

import useSWR, { type SWRResponse } from "swr";

import { apiJson } from "./client";
import type {
  AnnotationListResponse,
  AppSettings,
  CommentTemplateListResponse,
  ExamTasksResponse,
  Paper,
  PaperStatusSnapshot,
  ProjectListResponse,
  RasterCatalog,
  RatingsResponse,
  SearchResponse,
  TranscriptResponse,
} from "./types";

export interface GradeDistributionResponse {
  score_format: string;
  distribution: { label: string; count: number }[];
  total_papers: number;
  exported_papers: number;
}

function fetcher<T>(url: string): Promise<T> {
  return apiJson<T>(url);
}

/** Navigator tree data. ``archived`` toggles active vs. archived. ``tourOnly``
 * fetches only the guided-tour project (hides real data during the tour). */
export function useProjects(archived = false, tourOnly = false): SWRResponse<ProjectListResponse, Error> {
  const url = tourOnly
    ? "/api/projects/?tour_only=1"
    : `/api/projects/?archived=${archived ? 1 : 0}`;
  return useSWR<ProjectListResponse>(url, fetcher<ProjectListResponse>);
}

/** Full paper metadata. Pass `null` to skip the fetch (e.g. before a paper is
 * selected). */
export function usePaper(paperId: number | null): SWRResponse<Paper, Error> {
  return useSWR<Paper>(
    paperId == null ? null : `/api/papers/${paperId}`,
    fetcher<Paper>,
  );
}

/** Annotations for the paper, server-ordered for review-card grouping. */
export function useAnnotations(
  paperId: number | null,
): SWRResponse<AnnotationListResponse, Error> {
  return useSWR<AnnotationListResponse>(
    paperId == null ? null : `/api/papers/${paperId}/annotations`,
    fetcher<AnnotationListResponse>,
  );
}

/** Spec §8 polling hook. Auto-polls every 3 s while the paper is in
 * ``queued`` or ``processing``; stops polling once it reaches a terminal
 * state to keep the network quiet. */
export function usePaperStatus(
  paperId: number | null,
): SWRResponse<PaperStatusSnapshot, Error> {
  return useSWR<PaperStatusSnapshot>(
    paperId == null ? null : `/api/papers/${paperId}/status`,
    fetcher<PaperStatusSnapshot>,
    {
      refreshInterval: (latest) =>
        latest && (latest.status === "queued" || latest.status === "staged" || latest.status === "processing")
          ? 1000
          : 0,
      revalidateOnFocus: false,
    },
  );
}

/** Per-criterion raster ratings + the server-authoritative combined score
 * (docs/05-raster-grading.md). Pass ``null`` to skip. Only meaningful for
 * projects in raster grading mode; ``result`` is null otherwise. */
export function usePaperRatings(
  paperId: number | null,
): SWRResponse<RatingsResponse, Error> {
  return useSWR<RatingsResponse>(
    paperId == null ? null : `/api/papers/${paperId}/ratings`,
    fetcher<RatingsResponse>,
  );
}

/** Confirmed + proposed Exam tasks for a project. Pass ``null`` to skip. */
export function useExamTasks(projectId: number | null): SWRResponse<ExamTasksResponse, Error> {
  return useSWR<ExamTasksResponse>(
    projectId == null ? null : `/api/projects/${projectId}/tasks`,
    fetcher<ExamTasksResponse>,
  );
}

/** Grade distribution (count of papers per grade bucket in the score format). */
export function useGradeDistribution(
  projectId: number | null,
): SWRResponse<GradeDistributionResponse, Error> {
  return useSWR<GradeDistributionResponse>(
    projectId == null ? null : `/api/projects/${projectId}/grade_distribution`,
    fetcher<GradeDistributionResponse>,
  );
}

/** Static Hessen raster catalog (criteria + German band texts). Cached
 * indefinitely — it never changes at runtime. */
export function useRasterCatalog(): SWRResponse<RasterCatalog, Error> {
  return useSWR<RasterCatalog>("/api/grading/raster", fetcher<RasterCatalog>, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    dedupingInterval: 1000 * 60 * 60,
  });
}

/** Whole settings object (spec §6.7). Server overlays defaults for missing keys. */
export function useSettings(): SWRResponse<AppSettings, Error> {
  return useSWR<AppSettings>("/api/settings/", fetcher<AppSettings>);
}

/** Comment templates (spec §6.12). Filterable by type and language tag. */
export function useCommentTemplates(
  type?: string,
  languageTag?: string,
): SWRResponse<CommentTemplateListResponse, Error> {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (languageTag) params.set("language_tag", languageTag);
  const qs = params.toString();
  return useSWR<CommentTemplateListResponse>(
    `/api/comment_templates/${qs ? `?${qs}` : ""}`,
    fetcher<CommentTemplateListResponse>,
  );
}

/** Full OCR word list for the transcript view. Reads from the server-side
 * ``ocr.json`` written by the pipeline. Pass ``null`` to skip the fetch. */
export function usePaperTranscript(paperId: number | null): SWRResponse<TranscriptResponse, Error> {
  return useSWR<TranscriptResponse>(
    paperId == null ? null : `/api/papers/${paperId}/transcript`,
    fetcher<TranscriptResponse>,
  );
}

/** Cross-project search (spec §11.13). Returns null SWR when query is empty
 * so the hook doesn't fire a request on every keystroke before the user
 * types anything. */
export function useSearch(query: string): SWRResponse<SearchResponse, Error> {
  const term = query.trim();
  return useSWR<SearchResponse>(
    term ? `/api/projects/search?q=${encodeURIComponent(term)}` : null,
    fetcher<SearchResponse>,
  );
}
